import { useState, useMemo } from 'react';
import { Typography, Card, DatePicker, Input, Button, Table, Tag, Space, Tooltip } from 'antd';
import { SearchOutlined, ApartmentOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { JournalEntryItem, JournalEntryFilters } from '@/types/accounting';
import { useJournalEntries } from '@hooks/useAccounting';
import ReferencePicker from '@components/forms/ReferencePicker';

const { RangePicker } = DatePicker;

const acqDocTypeColor: Record<string, string> = {
  bank_receipt: 'green',
  bank_payment: 'red',
  cash_receipt: 'cyan',
  cash_expense: 'orange',
};

const acqDocTypeLabel: Record<string, string> = {
  bank_receipt: 'Банк (надх.)',
  bank_payment: 'Банк (вид.)',
  cash_receipt: 'Каса (надх.)',
  cash_expense: 'Каса (вид.)',
};

export default function JournalEntriesPage() {
  const acqYearStart = useMemo(() => dayjs().startOf('year'), []);
  const acqToday = useMemo(() => dayjs(), []);

  const [acqDateRange, acqSetDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([acqYearStart, acqToday]);
  const [acqAccountCode, acqSetAccountCode] = useState('');
  const [acqOrganizationId, acqSetOrganizationId] = useState<string | null>(null);
  const [acqPage, acqSetPage] = useState(1);
  const [acqPageSize, acqSetPageSize] = useState(50);

  const acqFilters: JournalEntryFilters = useMemo(() => ({
    dateFrom: acqDateRange[0].format('YYYY-MM-DD'),
    dateTo: acqDateRange[1].format('YYYY-MM-DD'),
    accountCode: acqAccountCode || undefined,
    organizationId: acqOrganizationId ?? undefined,
    page: acqPage,
    pageSize: acqPageSize,
  }), [acqDateRange, acqAccountCode, acqOrganizationId, acqPage, acqPageSize]);

  const { data: acqData, isLoading } = useJournalEntries(acqFilters);

  const acqItems = acqData?.items ?? [];
  const acqTotalCount = acqData?.totalCount ?? 0;

  const acqTotalAmount = useMemo(
    () => acqItems.reduce((acqAcc: number, acqRow: JournalEntryItem) => acqAcc + acqRow.amount, 0),
    [acqItems],
  );

  const acqColumns: ColumnsType<JournalEntryItem> = [
    {
      title: 'Дата',
      dataIndex: 'entryDate',
      key: 'entryDate',
      width: 100,
      render: (acqVal: string) => dayjs(acqVal).format('DD.MM.YYYY'),
    },
    {
      title: 'Організація',
      key: 'organization',
      width: 200,
      ellipsis: true,
      render: (_: unknown, acqRecord: JournalEntryItem) => {
        if (!acqRecord.organizationCode) return <Typography.Text type="secondary">—</Typography.Text>;
        const acqLabel = acqRecord.organizationName ?? acqRecord.organizationCode;
        return (
          <Tooltip title={`code: ${acqRecord.organizationCode}`}>
            <Tag icon={<ApartmentOutlined />} style={{ margin: 0 }}>{acqLabel}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Документ',
      dataIndex: 'docNumber',
      key: 'docNumber',
      width: 200,
      render: (_: string | undefined, acqRecord: JournalEntryItem) => (
        <Space size={4}>
          {acqRecord.docType && (
            <Tag color={acqDocTypeColor[acqRecord.docType] ?? 'default'} style={{ margin: 0 }}>
              {acqDocTypeLabel[acqRecord.docType] ?? acqRecord.docType}
            </Tag>
          )}
          <a
            href={`#dynamic-doc:${acqRecord.documentId}`}
            onClick={(e) => {
              e.preventDefault();
              // Перехід через хеш — MainLayout має слухати "dynamic-doc:" префікс і відкривати документ.
              // Якщо handler ще не реалізовано — буде no-op (посилання видиме як індикатор).
              window.location.hash = `dynamic-doc:${acqRecord.documentId}`;
            }}
            title="Відкрити документ"
          >
            {acqRecord.docNumber ?? '—'}
          </a>
        </Space>
      ),
    },
    {
      title: 'Дт',
      dataIndex: 'debitAccount',
      key: 'dt',
      width: 80,
      render: (acqVal: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{acqVal}</span>,
    },
    {
      title: 'Аналітика Дт',
      key: 'dtAnalytics',
      width: 120,
      render: (_: unknown, acqRecord: JournalEntryItem) =>
        acqRecord.dtSubconto1Type ? <Tag>{acqRecord.dtSubconto1Type}</Tag> : null,
    },
    {
      title: 'Кт',
      dataIndex: 'creditAccount',
      key: 'ct',
      width: 80,
      render: (acqVal: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{acqVal}</span>,
    },
    {
      title: 'Аналітика Кт',
      key: 'ctAnalytics',
      width: 120,
      render: (_: unknown, acqRecord: JournalEntryItem) =>
        acqRecord.ctSubconto1Type ? <Tag>{acqRecord.ctSubconto1Type}</Tag> : null,
    },
    {
      title: 'Сума',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right' as const,
      render: (acqVal: number) => acqVal.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Вал.',
      dataIndex: 'amountCurrency',
      key: 'amountCurrency',
      width: 120,
      align: 'right' as const,
      render: (acqVal: number | undefined, acqRecord: JournalEntryItem) =>
        acqVal ? `${acqVal.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} ${acqRecord.currencyCode ?? ''}` : '',
    },
    {
      title: 'Опис',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  const acqHandleSearch = () => {
    acqSetPage(1);
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>Журнал проводок</Typography.Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            value={acqDateRange}
            onChange={(acqDates) => {
              if (acqDates && acqDates[0] && acqDates[1]) {
                acqSetDateRange([acqDates[0], acqDates[1]]);
              }
            }}
            format="DD.MM.YYYY"
          />
          <div style={{ width: 240 }}>
            <ReferencePicker
              acqReferenceType="organization"
              acqValue={acqOrganizationId}
              acqOnChange={(acqId) => { acqSetOrganizationId(acqId); acqSetPage(1); }}
              acqAllowClear
              acqSize="middle"
            />
          </div>
          <Input
            placeholder="Рахунок (Дт або Кт)"
            value={acqAccountCode}
            onChange={(acqE) => acqSetAccountCode(acqE.target.value)}
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={acqHandleSearch}>
            Сформувати
          </Button>
        </Space>
      </Card>

      <Table<JournalEntryItem>
        columns={acqColumns}
        dataSource={acqItems}
        rowKey="id"
        loading={isLoading}
        bordered
        size="small"
        scroll={{ x: 1400 }}
        pagination={{
          current: acqPage,
          pageSize: acqPageSize,
          total: acqTotalCount,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (acqP, acqPs) => { acqSetPage(acqP); acqSetPageSize(acqPs); },
          showTotal: (acqTotal) => `Всього: ${acqTotal}`,
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <strong>Разом на сторінці:</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{acqTotalAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
}
